import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { FormRow } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import {
	useAdminRolesPermissions,
	useAdminUser,
	useCreateAdminUser,
	useUpdateAdminUser,
} from "@/hooks/use-admin-users";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { useCategoriesList } from "@/hooks/use-catalog";

const schema = z.object({
	email: z.string().email("Email invalide"),
	firstName: z.string().min(1, "Prénom requis").max(80),
	lastName: z.string().min(1, "Nom requis").max(80),
	phone: z.string().max(30).optional().or(z.literal("")),
	roleKey: z.string().min(1, "Rôle requis"),
	lang: z.enum(["fr", "en"]).default("fr"),
});

type FormValues = z.infer<typeof schema>;

interface UserFormPageProps {
	mode: "create" | "edit";
}

export function UserFormPage({ mode }: UserFormPageProps) {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const isEdit = mode === "edit";

	const { data: rolesData } = useAdminRolesPermissions();
	const { data: existingUser } = useAdminUser(isEdit ? id : undefined);
	const createMutation = useCreateAdminUser();
	const updateMutation = useUpdateAdminUser(id ?? "");

	const [selectedPerms, setSelectedPerms] = useState<Set<string>>(new Set());
	const [selectedCategories, setSelectedCategories] = useState<Set<string>>(
		new Set(),
	);
	const [error, setError] = useState<string | null>(null);

	const { data: categoriesData } = useCategoriesList({ isActive: true });

	const {
		register,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			email: "",
			firstName: "",
			lastName: "",
			phone: "",
			roleKey: "csr",
			lang: "fr",
		},
	});

	useEffect(() => {
		if (existingUser && isEdit) {
			reset({
				email: existingUser.email,
				firstName: existingUser.firstName,
				lastName: existingUser.lastName,
				phone: existingUser.phone ?? "",
				roleKey: existingUser.role.key,
				lang: (existingUser.lang as "fr" | "en") ?? "fr",
			});
			setSelectedPerms(
				new Set(existingUser.additionalPermissions.map((p) => p.key)),
			);
			setSelectedCategories(
				new Set(existingUser.assignedCategories.map((c) => c.id)),
			);
		}
	}, [existingUser, isEdit, reset]);

	const currentRoleKey = watch("roleKey");
	const currentRole = rolesData?.roles.find((r) => r.key === currentRoleKey);
	const isOperateur = currentRoleKey === "operateur";

	const togglePerm = (key: string) => {
		const next = new Set(selectedPerms);
		if (next.has(key)) next.delete(key);
		else next.add(key);
		setSelectedPerms(next);
	};

	const toggleCategory = (id: string) => {
		const next = new Set(selectedCategories);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		setSelectedCategories(next);
	};

	// Catégories ordonnées en arbre (parent → enfants), avec profondeur pour l'indentation.
	const orderedCategories: { id: string; name: string; depth: number }[] = [];
	{
		const cats = categoriesData ?? [];
		const presentIds = new Set(cats.map((c) => c.id));
		const byParent = new Map<string, typeof cats>();
		for (const c of cats) {
			if (!c.parentId) continue;
			const arr = byParent.get(c.parentId) ?? [];
			arr.push(c);
			byParent.set(c.parentId, arr);
		}
		const seen = new Set<string>();
		const pushSubtree = (c: (typeof cats)[number], depth: number) => {
			if (seen.has(c.id)) return; // garde anti-cycle
			seen.add(c.id);
			orderedCategories.push({ id: c.id, name: c.name, depth });
			const children = [...(byParent.get(c.id) ?? [])].sort(
				(a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
			);
			for (const child of children) pushSubtree(child, depth + 1);
		};
		// Racines = niveau 1 OU catégorie dont le parent est absent de la liste (orphelin
		// dont le parent est inactif/archivé) → aucune catégorie n'est inatteignable.
		cats
			.filter((c) => !c.parentId || !presentIds.has(c.parentId))
			.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
			.forEach((c) => pushSubtree(c, 0));
	}

	const submit = handleSubmit(async (data) => {
		setError(null);
		try {
			// Le périmètre catalogue ne concerne que les opérateurs ; sinon on envoie
			// une liste vide pour purger un éventuel périmètre résiduel côté API.
			const assignedCategoryIds =
				data.roleKey === "operateur" ? Array.from(selectedCategories) : [];
			if (isEdit && id) {
				await updateMutation.mutateAsync({
					firstName: data.firstName,
					lastName: data.lastName,
					phone: data.phone || undefined,
					roleKey: data.roleKey,
					lang: data.lang,
					additionalPermissions: Array.from(selectedPerms),
					assignedCategoryIds,
				});
				navigate(`/administration/utilisateurs/${id}`);
			} else {
				const created = await createMutation.mutateAsync({
					email: data.email,
					firstName: data.firstName,
					lastName: data.lastName,
					phone: data.phone || undefined,
					roleKey: data.roleKey,
					lang: data.lang,
					additionalPermissions: Array.from(selectedPerms),
					assignedCategoryIds,
				});
				navigate(`/administration/utilisateurs/${created.id}`);
			}
		} catch (err) {
			setError(extractErrorMessage(err, "Enregistrement impossible"));
		}
	});

	// Permissions du rôle (héritées) — affichées pour info, non décochables
	const rolePerms = new Set(currentRole?.permissions ?? []);
	// Permissions disponibles (toutes), hors celles déjà dans le rôle
	type Perm = { key: string; label: string; module: string };
	const permsByModule = new Map<string, Perm[]>();
	for (const p of rolesData?.permissions ?? []) {
		if (rolePerms.has(p.key)) continue; // skip celles déjà dans le rôle
		const arr = permsByModule.get(p.module) ?? [];
		arr.push(p);
		permsByModule.set(p.module, arr);
	}

	const isSubmitting = createMutation.isPending || updateMutation.isPending;

	return (
		<>
			<div className="crumbs" style={{ marginBottom: 14, fontSize: 12 }}>
				<a
					className="root"
					style={{ cursor: "pointer" }}
					onClick={() => navigate("/administration")}
				>
					Administration
				</a>
				<span className="sep">
					<AdminIcon n="chevR" s={10} />
				</span>
				<span className="here">
					{isEdit ? "Modifier" : "Nouvel utilisateur"}
				</span>
			</div>

			<PageHead
				title={
					isEdit
						? `Modifier ${existingUser?.fullName ?? "…"}`
						: "Nouvel utilisateur"
				}
				sub={
					isEdit
						? "Les changements de rôle entraînent un recalcul des permissions effectives."
						: "Un mail d'invitation sera envoyé à l'email renseigné (lien d'activation valide 24h)."
				}
				actions={
					<>
						<AdminButton
							variant="ghost"
							onClick={() => navigate("/administration")}
						>
							Annuler
						</AdminButton>
						<AdminButton
							variant="primary"
							onClick={submit}
							disabled={isSubmitting}
							data-testid="user-form-submit"
						>
							{isSubmitting
								? "Enregistrement…"
								: isEdit
									? "Enregistrer"
									: "Créer & inviter"}
							<AdminIcon n="check" s={14} c="#fff" />
						</AdminButton>
					</>
				}
			/>

			{error && (
				<div
					className="auth-alert danger"
					data-testid="user-form-error"
					style={{ marginBottom: 14 }}
				>
					<span className="icon">
						<AdminIcon n="alert" s={16} />
					</span>
					<div>{error}</div>
				</div>
			)}

			<form onSubmit={submit} noValidate>
				<div className="grid-2e">
					<Card>
						<CardHead title="Identité" />
						<CardBody>
							<FormRow
								label="Email professionnel"
								required
								helper={isEdit ? "Non modifiable" : undefined}
							>
								<input
									className="aform-i"
									type="email"
									disabled={isEdit}
									data-testid="user-form-email"
									{...register("email")}
								/>
								{errors.email && (
									<div
										style={{
											color: "var(--a-danger)",
											fontSize: 11,
											marginTop: 4,
										}}
									>
										{errors.email.message}
									</div>
								)}
							</FormRow>
							<div
								style={{
									display: "grid",
									gridTemplateColumns: "1fr 1fr",
									gap: 16,
								}}
							>
								<FormRow label="Prénom" required>
									<input
										className="aform-i"
										data-testid="user-form-firstname"
										{...register("firstName")}
									/>
									{errors.firstName && (
										<div
											style={{
												color: "var(--a-danger)",
												fontSize: 11,
												marginTop: 4,
											}}
										>
											{errors.firstName.message}
										</div>
									)}
								</FormRow>
								<FormRow label="Nom" required>
									<input
										className="aform-i"
										data-testid="user-form-lastname"
										{...register("lastName")}
									/>
									{errors.lastName && (
										<div
											style={{
												color: "var(--a-danger)",
												fontSize: 11,
												marginTop: 4,
											}}
										>
											{errors.lastName.message}
										</div>
									)}
								</FormRow>
							</div>
							<FormRow label="Téléphone">
								<input
									className="aform-i"
									placeholder="+237 6 99 12 34 56"
									{...register("phone")}
								/>
							</FormRow>
							<FormRow label="Langue" required>
								<select className="aform-i" {...register("lang")}>
									<option value="fr">Français</option>
									<option value="en">English</option>
								</select>
							</FormRow>
						</CardBody>
					</Card>

					<Card>
						<CardHead
							title="Rôle principal"
							sub="Les permissions par défaut sont héritées du rôle"
						/>
						<CardBody>
							<FormRow label="Rôle" required>
								<select
									className="aform-i"
									data-testid="user-form-role"
									{...register("roleKey")}
								>
									{rolesData?.roles.map((r) => (
										<option key={r.key} value={r.key}>
											{r.label}
										</option>
									))}
								</select>
							</FormRow>
							{currentRole && (
								<>
									<div
										className="muted"
										style={{ fontSize: 12, marginBottom: 8 }}
									>
										{currentRole.description}
									</div>
									<div className="eyebrow" style={{ marginBottom: 6 }}>
										{currentRole.permissions.length} permission(s) héritée(s)
									</div>
									<div
										style={{
											display: "flex",
											flexWrap: "wrap",
											gap: 4,
											maxHeight: 200,
											overflow: "auto",
										}}
									>
										{currentRole.permissions.map((p) => (
											<span
												key={p}
												className="mono"
												style={{
													fontSize: 10,
													padding: "2px 6px",
													background: "var(--a-surface-2)",
													borderRadius: 3,
												}}
											>
												{p}
											</span>
										))}
										{currentRole.permissions.length === 0 && (
											<span className="muted" style={{ fontSize: 11 }}>
												(Super Admin : toutes les permissions via wildcard)
											</span>
										)}
									</div>
								</>
							)}
						</CardBody>
					</Card>
				</div>

				<Card style={{ marginTop: 16 }}>
					<CardHead
						title={`Permissions additionnelles · ${selectedPerms.size} sélectionnée(s)`}
						sub="Permissions atomiques accordées exceptionnellement (CDC §4.5.3)"
						right={
							selectedPerms.size > 0 && (
								<AdminButton
									variant="ghost"
									size="sm"
									onClick={() => setSelectedPerms(new Set())}
								>
									Tout désélectionner
								</AdminButton>
							)
						}
					/>
					<CardBody>
						<div className="grid-3">
							{Array.from(permsByModule.entries()).map(([module, perms]) => (
								<div key={module}>
									<div className="eyebrow" style={{ marginBottom: 6 }}>
										{module}
									</div>
									{perms.map((p) => (
										<label
											key={p.key}
											style={{
												display: "flex",
												alignItems: "flex-start",
												gap: 8,
												padding: "5px 0",
												fontSize: 12,
												cursor: "pointer",
											}}
										>
											<input
												type="checkbox"
												checked={selectedPerms.has(p.key)}
												onChange={() => togglePerm(p.key)}
												data-testid={`perm-${p.key}`}
											/>
											<span>
												<span className="mono" style={{ fontSize: 11 }}>
													{p.key}
												</span>
												<div className="muted" style={{ fontSize: 11 }}>
													{p.label}
												</div>
											</span>
										</label>
									))}
								</div>
							))}
						</div>
					</CardBody>
				</Card>

				{isOperateur && (
					<Card style={{ marginTop: 16 }}>
						<CardHead
							title={`Périmètre catalogue · ${selectedCategories.size} catégorie(s)`}
							sub="L'opérateur ne voit et ne gère que les produits des catégories cochées. Cocher une catégorie parente inclut automatiquement ses sous-catégories."
							right={
								selectedCategories.size > 0 && (
									<AdminButton
										variant="ghost"
										size="sm"
										onClick={() => setSelectedCategories(new Set())}
									>
										Tout désélectionner
									</AdminButton>
								)
							}
						/>
						<CardBody>
							{orderedCategories.length === 0 ? (
								<div className="muted" style={{ fontSize: 12 }}>
									Aucune catégorie active disponible.
								</div>
							) : (
								<>
									{selectedCategories.size === 0 && (
										<div
											className="auth-alert warn"
											style={{ marginBottom: 12 }}
										>
											<span className="icon">
												<AdminIcon n="alert" s={16} />
											</span>
											<div>
												Sans catégorie assignée, cet opérateur ne verra aucun
												produit.
											</div>
										</div>
									)}
									<div style={{ maxHeight: 320, overflow: "auto" }}>
										{orderedCategories.map((c) => (
											<label
												key={c.id}
												style={{
													display: "flex",
													alignItems: "center",
													gap: 8,
													padding: "5px 0",
													paddingLeft: c.depth * 18,
													fontSize: 13,
													cursor: "pointer",
												}}
											>
												<input
													type="checkbox"
													checked={selectedCategories.has(c.id)}
													onChange={() => toggleCategory(c.id)}
													data-testid={`cat-${c.id}`}
												/>
												<span>{c.name}</span>
											</label>
										))}
									</div>
								</>
							)}
						</CardBody>
					</Card>
				)}

				{!isEdit && (
					<div className="auth-alert info" style={{ marginTop: 14 }}>
						<span className="icon">
							<AdminIcon n="info" s={16} />
						</span>
						<div>
							<strong>Un mail d'invitation sera envoyé</strong>
							L'utilisateur recevra un lien valide 24h pour définir son mot de
							passe initial.
						</div>
					</div>
				)}
			</form>
		</>
	);
}

export function UserCreatePage() {
	return <UserFormPage mode="create" />;
}
export function UserEditPage() {
	return <UserFormPage mode="edit" />;
}
